"use client";

import { useEffect, useId, useState, type CSSProperties } from "react";
import { cn } from "@newxsystems/shared";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@templates/design-system";
import { resolveKaajPrimitive } from "../../../../client";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";
import { KaajAction } from "../../../../cms/components/blocks/_shared/KaajAction";
import type { ImageCarouselItem } from "../../../../cms/components/blocks/ImageCarouselBlock/ImageCarouselBlock.props";
import type { IKaajImageCarouselProps } from "./KaajImageCarousel.props";

const formatIndex = (value: number) => String(value).padStart(2, "0");

type ImageCarouselImage = ImageCarouselItem["image"];

const getObjectPosition = (image: ImageCarouselImage): CSSProperties | undefined =>
  image.focalPoint
    ? { objectPosition: `${image.focalPoint.x}% ${image.focalPoint.y}%` }
    : undefined;

const ArrowIcon = ({ direction }: { direction: "next" | "previous" }) => (
  <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 16 16">
    <path
      d={direction === "next" ? "M6 3.5 10.5 8 6 12.5" : "M10 3.5 5.5 8l4.5 4.5"}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
  </svg>
);

export const KaajImageCarousel = ({ items, behavior, className }: IKaajImageCarouselProps) => {
  const Image = resolveKaajPrimitive("Image");
  const carouselId = useId();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [autoplayAllowed, setAutoplayAllowed] = useState(false);
  const slides = items ?? [];
  const loop = behavior?.loop === true;
  const snapCount = count || slides.length;
  const activeSnap = Math.min(current + 1, snapCount || 1);
  const autoplayConfigured = Boolean(behavior?.autoplay);
  const autoplayActive = autoplayAllowed && !isPaused && !isInteracting;

  useEffect(() => {
    if (!api) return;

    const updateCarouselState = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateCarouselState();
    api.on("select", updateCarouselState);
    api.on("reInit", updateCarouselState);

    return () => {
      api.off("select", updateCarouselState);
      api.off("reInit", updateCarouselState);
    };
  }, [api]);

  useEffect(() => {
    if (!behavior?.autoplay) {
      setAutoplayAllowed(false);
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateAutoplayAvailability = () => setAutoplayAllowed(!mediaQuery.matches);

    updateAutoplayAvailability();
    mediaQuery.addEventListener("change", updateAutoplayAvailability);

    return () => {
      mediaQuery.removeEventListener("change", updateAutoplayAvailability);
    };
  }, [behavior?.autoplay]);

  useEffect(() => {
    if (!api || !autoplayActive) return;

    const interval = window.setInterval(() => {
      if (!loop && !api.canScrollNext()) {
        setIsPaused(true);
        return;
      }

      api.scrollNext();
    }, 5000);

    return () => window.clearInterval(interval);
  }, [api, autoplayActive, loop]);

  if (!slides.length) return null;

  return (
    <Carousel
      aria-label="Image carousel"
      className={cn("relative w-full", className)}
      onBlurCapture={(event) => {
        const nextFocusedElement = event.relatedTarget;

        if (
          !(nextFocusedElement instanceof Node) ||
          !event.currentTarget.contains(nextFocusedElement)
        ) {
          setIsInteracting(false);
        }
      }}
      onFocusCapture={() => setIsInteracting(true)}
      onMouseEnter={() => setIsInteracting(true)}
      onMouseLeave={() => setIsInteracting(false)}
      opts={{ align: "start", loop }}
      setApi={setApi}
    >
      <p aria-live={autoplayActive ? "off" : "polite"} className="sr-only">
        Showing carousel position {activeSnap} of {snapCount || slides.length}.
      </p>

      <div className="relative overflow-hidden rounded-[2rem] border border-[#D9E1FF] bg-[#FAFBFF] p-2 [contain:layout_paint] sm:p-3 lg:rounded-[2.35rem] lg:p-4">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#B8C7FF] to-transparent"
        />
        <CarouselContent
          className="-ml-0 gap-x-4 will-change-transform"
          viewportClassName="relative rounded-[1.7rem] [contain:layout_paint]"
        >
          {slides.map((item, index) => {
            const image = item.image;
            const headline = item.caption;
            const credit = item.credit;
            const tags = item.tags ?? [];
            const imageWidth = image.width ?? item.width;
            const imageHeight = image.height ?? item.height;
            const imageAspectRatio = item.aspectRatio ?? undefined;
            const slideId = `${carouselId}-slide-${index + 1}`;

            return (
              <CarouselItem
                className="basis-full pl-0"
                id={slideId}
                key={`${image.defaultSrc}-${index}`}
              >
                <figure
                  className={cn(
                    "relative h-full min-w-0 overflow-hidden rounded-[1.7rem] border border-[#D9E1FF]/90 bg-white",
                    "lg:grid lg:grid-cols-[minmax(0,1.48fr)_minmax(19rem,0.62fr)]",
                  )}
                >
                  <div
                    className="relative min-h-[220px] overflow-hidden bg-[#EEF2FF] sm:min-h-[300px] lg:min-h-[420px]"
                    style={{ aspectRatio: imageAspectRatio }}
                  >
                    <Image
                      alt={image.alternateText}
                      className="relative z-10 h-full w-full object-cover"
                      decoding="async"
                      height={imageHeight ?? undefined}
                      loading={index === 0 ? "eager" : "lazy"}
                      sizes={
                        image.sizes ?? "(min-width: 1280px) 46rem, (min-width: 1024px) 62vw, 100vw"
                      }
                      src={image.defaultSrc}
                      srcSet={image.srcSet ?? undefined}
                      style={getObjectPosition(image)}
                      width={imageWidth ?? undefined}
                    />
                    <div className="pointer-events-none absolute inset-0 z-20 ring-1 ring-inset ring-white/30" />
                  </div>

                  <figcaption className="flex min-w-0 flex-1 flex-col gap-4 p-5 sm:p-6 lg:p-7">
                    <div className="flex flex-wrap items-center gap-2">
                      {item.category ? (
                        <span className="inline-flex max-w-full rounded-full bg-[#EEF2FF] px-3 py-1 text-[0.68rem] font-extrabold uppercase leading-none tracking-[0.16em] text-[#2144FF] [overflow-wrap:anywhere]">
                          {item.category}
                        </span>
                      ) : null}
                      {tags.map((tag, tagIndex) => (
                        <span
                          className="inline-flex max-w-full rounded-full border border-[#D9E1FF] bg-white px-3 py-1 text-xs font-semibold leading-none text-[#405076] [overflow-wrap:anywhere]"
                          key={`${tag.tag}-${tagIndex}`}
                        >
                          {tag.tag}
                        </span>
                      ))}
                    </div>

                    {headline ? (
                      <p className="text-pretty text-2xl font-extrabold leading-[1.08] tracking-[-0.025em] text-[#151927] [overflow-wrap:anywhere] lg:text-[1.7rem]">
                        {headline}
                      </p>
                    ) : null}

                    {item.content ? (
                      <RichTextHtml
                        className="text-pretty text-base leading-7 text-[#4B5568] [&_a]:font-bold [&_a]:text-[#2144FF] [&_a]:underline [&_a]:underline-offset-4 [&_li]:ml-5 [&_li]:list-disc [&_p+p]:mt-3"
                        html={item.content}
                      />
                    ) : null}

                    {item.buttons?.length ? (
                      <div className="flex flex-wrap gap-3">
                        {item.buttons.map((button, buttonIndex) => (
                          <KaajAction
                            ariaLabel={button.ariaLabel}
                            href={button.href}
                            key={`${button.href}-${button.text}-${buttonIndex}`}
                            label={button.text}
                            target={button.target}
                            variant={button.style}
                          />
                        ))}
                      </div>
                    ) : null}

                    {credit ? (
                      <div className="mt-auto grid gap-3 border-t border-[#E6ECFF] pt-4 text-sm leading-6">
                        <p className="font-semibold text-[#667085]">{credit}</p>
                      </div>
                    ) : null}
                  </figcaption>
                </figure>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </div>

      {(behavior?.controls === true && snapCount > 1) || autoplayConfigured ? (
        <div className="mt-5 grid gap-4 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center">
          {behavior?.controls === true && snapCount > 1 ? (
            <div className="flex items-center justify-center gap-3 lg:justify-start">
              <button
                aria-label="Show previous carousel image"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-[#D9E1FF] bg-white text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.12)] outline-none transition-[border-color,box-shadow,transform,color] duration-200 hover:border-[#2144FF] hover:text-[#132EDB] focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4 disabled:cursor-not-allowed disabled:border-[#E6ECFF] disabled:bg-[#FAFBFF] disabled:text-[#9AA7C7] motion-reduce:transition-none motion-safe:hover:-translate-y-0.5"
                disabled={!loop && !canScrollPrev}
                onClick={() => api?.scrollPrev()}
                type="button"
              >
                <ArrowIcon direction="previous" />
              </button>
              <button
                aria-label="Show next carousel image"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full bg-[#2144FF] text-white shadow-[0_14px_32px_rgba(33,68,255,0.24)] outline-none transition-[background-color,box-shadow,transform] duration-200 hover:bg-[#132EDB] focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4 disabled:cursor-not-allowed disabled:bg-[#BFCBFF] disabled:text-white motion-reduce:transition-none motion-safe:hover:-translate-y-0.5"
                disabled={!loop && !canScrollNext}
                onClick={() => api?.scrollNext()}
                type="button"
              >
                <ArrowIcon direction="next" />
              </button>
              <span className="rounded-full border border-[#D9E1FF] bg-white px-3 py-2 text-sm font-extrabold text-[#4B5568] shadow-[0_10px_24px_rgba(33,68,255,0.08)]">
                {formatIndex(activeSnap)} / {formatIndex(snapCount || slides.length)}
              </span>
            </div>
          ) : null}

          {behavior?.controls === true && snapCount > 1 ? (
            <div
              aria-label="Choose carousel image"
              className="min-w-0 overflow-x-auto rounded-[1.1rem] border border-[#D9E1FF] bg-white/86 p-1.5 shadow-[0_14px_34px_rgba(33,68,255,0.08)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex min-w-max gap-2">
                {slides.map((item, index) => {
                  const image = item.image;
                  const active = current === index;
                  const imageWidth = item.width;
                  const imageHeight = item.height;

                  return (
                    <button
                      aria-current={active ? "true" : undefined}
                      aria-label={`Show carousel image ${index + 1}`}
                      className={cn(
                        "group relative h-14 w-20 shrink-0 overflow-hidden rounded-[0.85rem] border bg-[#EEF2FF] outline-none transition-[border-color,box-shadow,transform] duration-200 focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-2 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 sm:w-24",
                        active
                          ? "border-[#2144FF] shadow-[0_10px_24px_rgba(33,68,255,0.18)]"
                          : "border-[#D9E1FF] hover:border-[#B8C7FF]",
                      )}
                      key={`${image.defaultSrc}-thumb-${index}`}
                      onClick={() => api?.scrollTo(index)}
                      type="button"
                    >
                      <Image
                        alt=""
                        aria-hidden="true"
                        className="h-full w-full object-cover transition duration-300 motion-reduce:transition-none motion-safe:group-hover:scale-[1.04]"
                        decoding="async"
                        height={imageHeight ?? undefined}
                        loading="lazy"
                        sizes="6rem"
                        src={image.defaultSrc}
                        style={getObjectPosition(image)}
                        width={imageWidth ?? undefined}
                      />
                      <span
                        className={cn(
                          "absolute inset-x-0 bottom-0 h-1 transition-colors",
                          active ? "bg-[#2144FF]" : "bg-white/70 group-hover:bg-[#B8C7FF]",
                        )}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {autoplayConfigured ? (
            <button
              aria-label={isPaused ? "Resume slideshow" : "Pause slideshow"}
              aria-pressed={isPaused}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-[#D9E1FF] bg-white px-4 py-2 text-sm font-extrabold text-[#2144FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2144FF]"
              onClick={() => setIsPaused((paused) => !paused)}
              type="button"
            >
              {isPaused ? "Resume" : "Pause"}
            </button>
          ) : null}
        </div>
      ) : null}
    </Carousel>
  );
};

KaajImageCarousel.displayName = "Interactive::KaajImageCarousel";
